import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { SENDS_TEXTS } from "@/shared/lib/send-texts";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useFunnelStore } from "@/store";
import { useMission } from "@/features/missionkit/api/use-mission";
import { doorKeys, missionIdFor, useDailyDoors } from "./use-doors";
import { opLink } from "../hooks/use-phone-login";
import type { Tier } from "@/features/funnel/types";

/** One debriefed mission in the operative's file. */
export interface FiledMission {
  glowId: number;
  name: string;
  whatIDid: string;
  howItFelt: string;
  /** Photo or video they attached; '' when none. */
  mediaUrl: string;
  /** "image" | "video" | "text" (nothing attached). */
  format: string;
  /** The mission's level, upper-case ("BEGINNER", "CHALLENGING",
   *  "MISSION IMPROBABLE"); "" when it could not be matched. */
  level?: string;
  filedAt: string | null;
}

export const fileKeys = { all: ["secretmissions", "file"] as const };

/** The operative's file — every mission they have debriefed, newest first. */
export const useMissionFile = () => {
  // a texted link's member: opened in a fresh browser, their missions still
  // count (the vault's lock, the mission number)
  const op = opLink();
  return useQuery({
    queryKey: [...fileKeys.all, op],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        result: { missions: FiledMission[] };
      }>(API_ENDPOINTS.mission.file, { params: op ? { op } : undefined });
      return data.result.missions;
    },
  });
};

/** What createpost's `mediaType` calls a file — its storage folder. */
const mediaTypeOf = (file: File) =>
  file.type.startsWith("video/") ? "video" : "image";

/**
 * File a debrief. It is a Glow post of Type "SecretMission": Title is the
 * mission name, content what they did, Description how it felt. An attached
 * photo/video goes as multipart `media`, the way Glow's own composer posts it.
 */
export const useFileDebrief = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (debrief: {
      missionName: string;
      missionId: number;
      shareAsExample: boolean;
      whatIDid: string;
      howItFelt: string;
      media?: File | null;
    }) => {
      const form = new FormData();
      form.append("Type", "SecretMission");
      form.append("Title", debrief.missionName);
      form.append("MissionID", String(debrief.missionId));
      form.append("ShareAsExample", debrief.shareAsExample ? "1" : "0");
      form.append("content", debrief.whatIDid);
      form.append("Description", debrief.howItFelt);
      form.append(
        "mediaType",
        debrief.media ? mediaTypeOf(debrief.media) : "text",
      );
      if (debrief.media) form.append("media", debrief.media);
      // The shared client defaults to JSON, which axios would apply to the
      // FormData by flattening it; multipart lets the browser add the boundary.
      return apiClient.post(API_ENDPOINTS.glow.createPost, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    // the doors skip filed missions: fetch the next ones fresh
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: fileKeys.all }),
        qc.invalidateQueries({ queryKey: doorKeys.today }),
      ]),
  });
};

/**
 * Book the next mission's text from the sign-off step. A query, not a
 * mutation: the endpoint is idempotent (an unfired booking is returned, not
 * doubled), so arriving on the screen, reloading it, and retrying are all the
 * same read. `at`: the time they picked on the well-done screen (ms epoch);
 * null leaves it to the step (tomorrow). Sent as minutes from now — the
 * server times it on its own clock.
 */
export const useNextDayBooking = (
  adventureId: number | string,
  stepName: string,
  at: number | null = null,
) => {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["secretmissions", "next-day", String(adventureId), stepName, at],
    queryFn: async () => {
      // a dev build against a real backend: book nothing (see SENDS_TEXTS)
      if (!SENDS_TEXTS)
        return {
          eventId: 0,
          fireAt: new Date().toISOString(),
          alreadyScheduled: false,
        };
      const { data } = await apiClient.post<{
        result: { eventId: number; fireAt: string; alreadyScheduled: boolean };
      }>(API_ENDPOINTS.mission.nextDay(adventureId), {
        stepName,
        ...(at !== null && {
          delayMinutes: Math.max(0, Math.round((at - Date.now()) / 60_000)),
        }),
      });
      // the day is booked: the next doors they see are tomorrow's
      void qc.invalidateQueries({ queryKey: doorKeys.today });
      return data.result;
    },
    enabled: !!stepName && at !== null,
    retry: false,
    // Only auto-book a time the member explicitly selected.
    // An explicit retry can still call refetch after a failed booking.
    staleTime: 0,
    gcTime: 0,
  });
};

/**
 * Book the texts a web step authors for this moment (its ScheduleEvent), e.g.
 * accepting a mission: the agency's contact card, then the chosen case with
 * the link back. The server resolves the case image from `tier`. Fire and
 * forget: the web flow never waits on a text.
 */
export const bookStepTexts = (
  adventureId: number | string,
  stepName: string,
  tier: Tier,
  missionId?: number | null,
) =>
  // a dev build against a real backend: book nothing (see SENDS_TEXTS)
  !SENDS_TEXTS
    ? Promise.resolve()
    : apiClient
        .post(API_ENDPOINTS.mission.nextDay(adventureId), {
          stepName,
          tier,
          missionId,
        })
        .catch(() => {
          /* no phone yet / step authors no texts — the web flow carries on */
        });

export interface Dossier {
  name: string;
  completed: number;
  locks: number;
  missions: { name: string; filedAt: string | null }[];
}

/** An operative's dossier, by the clearance token they shared. */
export const useDossier = (token: string) =>
  useQuery({
    queryKey: ["secretmissions", "dossier", token],
    queryFn: async () => {
      const { data } = await apiClient.get<{ result: Dossier }>(
        API_ENDPOINTS.mission.dossier(token),
      );
      return data.result;
    },
    retry: false,
  });

/** The session operative's clearance link, to share. */
export const fetchClearanceLink = async () => {
  const { data } = await apiClient.get<{ result: { token: string } }>(
    API_ENDPOINTS.mission.dossierLink,
  );
  return `${window.location.origin}/dossier/${data.result.token}`;
};

/** The mission they accepted: its id as saved on accept, or — if that was
 *  never saved — worked out again from the door and case they chose, the
 *  same way the case screen found it (so the live mission never loads
 *  empty, leaving its buttons dead). Worked out, it is saved then, so the
 *  doors moving on at midnight cannot swap it for another. */
export const useAcceptedMission = () => {
  const saved = useFunnelStore((s) => s.missionId);
  const door = useFunnelStore((s) => s.door);
  const tier = useFunnelStore((s) => s.tier ?? s.caseTier);
  const { data: doors } = useDailyDoors();
  const derived = saved ?? (tier ? missionIdFor(doors, door, tier) : null);
  useEffect(() => {
    if (saved == null && derived != null)
      useFunnelStore.setState({ missionId: derived });
  }, [saved, derived]);
  return useMission(derived);
};
